/**
 * CapabilityChecklist - Product-agnostic capability assessment questions
 *
 * This is the core component for the capability-first assessment approach.
 * Users answer yes/no questions about their capabilities, and the system
 * derives their maturity level based on what they have in place.
 *
 * Key features:
 * - Questions grouped by maturity level they imply
 * - Help text and evidence hints for each question
 * - Animated transitions for smooth UX
 * - Real-time maturity derivation feedback
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Check, X, HelpCircle, Lightbulb, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { MATURITY_LEVELS } from '../../../../config/zeroTrustConstants';
import MaturityLevelBadge, { MaturityLevelDot } from './MaturityLevelBadge';
import './CapabilityChecklist.css';

export default function CapabilityChecklist({
  questions = [], // Array of { questionId, questionKey, questionText, helpText, impliesLevel, evidenceHint }
  answers = {}, // Map of questionId -> { answer: boolean, evidenceNotes: string }
  onAnswerChange, // (questionId, answer, evidenceNotes) => void
  derivedLevel = null, // The level derived from current answers
  showDerivedFeedback = true,
  showEvidenceFields = true,
  groupByLevel = true,
  animated = true
}) {
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [showingHelpFor, setShowingHelpFor] = useState(null);

  // Group questions by the level they imply
  const questionsByLevel = useMemo(() => {
    if (!groupByLevel) return { ALL: questions };

    const grouped = {};
    MATURITY_LEVELS.forEach(level => {
      grouped[level.key] = questions.filter(q => q.impliesLevel === level.key);
    });
    return grouped;
  }, [questions, groupByLevel]);

  // Calculate progress for each level
  const levelProgress = useMemo(() => {
    const progress = {};
    MATURITY_LEVELS.forEach(level => {
      const levelQuestions = questions.filter(q => q.impliesLevel === level.key);
      const answeredYes = levelQuestions.filter(q => answers[q.questionId]?.answer === true).length;
      progress[level.key] = {
        total: levelQuestions.length,
        answered: answeredYes,
        percentage: levelQuestions.length > 0 ? (answeredYes / levelQuestions.length) * 100 : 0
      };
    });
    return progress;
  }, [questions, answers]);

  const handleAnswer = useCallback((questionId, answer) => {
    onAnswerChange?.(questionId, answer, answers[questionId]?.evidenceNotes || '');
  }, [onAnswerChange, answers]);

  const handleEvidenceChange = useCallback((questionId, notes) => {
    onAnswerChange?.(questionId, answers[questionId]?.answer, notes);
  }, [onAnswerChange, answers]);

  const toggleHelp = (questionId) => {
    setShowingHelpFor(showingHelpFor === questionId ? null : questionId);
  };

  const toggleEvidence = (questionId) => {
    setExpandedQuestion(expandedQuestion === questionId ? null : questionId);
  };

  return (
    <div className={`ccl-checklist ${animated ? 'ccl-checklist--animated' : ''}`}>
      {/* Derived Level Feedback */}
      {showDerivedFeedback && derivedLevel && (
        <div className="ccl-derived-feedback">
          <span className="ccl-derived-label">Derived Maturity:</span>
          <MaturityLevelBadge level={derivedLevel} size="md" animated />
          <span className="ccl-derived-hint">
            Based on {Object.values(answers).filter(a => a?.answer === true).length} capabilities
          </span>
        </div>
      )}

      {/* Questions by Level */}
      {groupByLevel ? (
        MATURITY_LEVELS.map((level, levelIdx) => {
          const levelQuestions = questionsByLevel[level.key] || [];
          if (levelQuestions.length === 0) return null;

          const progress = levelProgress[level.key];

          return (
            <div
              key={level.key}
              className="ccl-level-group"
              style={{
                '--level-color': level.color,
                animationDelay: animated ? `${levelIdx * 0.1}s` : '0s'
              }}
            >
              <div className="ccl-level-header">
                <div className="ccl-level-title">
                  <MaturityLevelBadge level={level.key} size="sm" variant="subtle" />
                  <span className="ccl-level-count">
                    {progress.answered}/{progress.total} capabilities
                  </span>
                </div>
                <div className="ccl-level-progress">
                  <div
                    className="ccl-level-progress-fill"
                    style={{ width: `${progress.percentage}%` }}
                  />
                </div>
              </div>

              <div className="ccl-questions">
                {levelQuestions.map((question, qIdx) => (
                  <QuestionItem
                    key={question.questionId}
                    question={question}
                    answer={answers[question.questionId]?.answer}
                    evidenceNotes={answers[question.questionId]?.evidenceNotes}
                    isHelpShowing={showingHelpFor === question.questionId}
                    isEvidenceExpanded={expandedQuestion === question.questionId}
                    onAnswer={handleAnswer}
                    onEvidenceChange={handleEvidenceChange}
                    onToggleHelp={() => toggleHelp(question.questionId)}
                    onToggleEvidence={() => toggleEvidence(question.questionId)}
                    showEvidenceFields={showEvidenceFields}
                    animated={animated}
                    index={qIdx}
                  />
                ))}
              </div>
            </div>
          );
        })
      ) : (
        <div className="ccl-questions ccl-questions--flat">
          {questions.map((question, qIdx) => (
            <QuestionItem
              key={question.questionId}
              question={question}
              answer={answers[question.questionId]?.answer}
              evidenceNotes={answers[question.questionId]?.evidenceNotes}
              isHelpShowing={showingHelpFor === question.questionId}
              isEvidenceExpanded={expandedQuestion === question.questionId}
              onAnswer={handleAnswer}
              onEvidenceChange={handleEvidenceChange}
              onToggleHelp={() => toggleHelp(question.questionId)}
              onToggleEvidence={() => toggleEvidence(question.questionId)}
              showEvidenceFields={showEvidenceFields}
              animated={animated}
              index={qIdx}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * QuestionItem - Individual capability question with yes/no toggle
 */
function QuestionItem({
  question,
  answer,
  evidenceNotes,
  isHelpShowing,
  isEvidenceExpanded,
  onAnswer,
  onEvidenceChange,
  onToggleHelp,
  onToggleEvidence,
  showEvidenceFields,
  animated,
  index
}) {
  const hasAnswer = answer !== undefined && answer !== null;
  const isYes = answer === true;
  const isNo = answer === false;

  return (
    <div
      className={`
        ccl-question
        ${hasAnswer ? 'ccl-question--answered' : ''}
        ${isYes ? 'ccl-question--yes' : ''}
        ${isNo ? 'ccl-question--no' : ''}
      `.trim().replace(/\s+/g, ' ')}
      style={{ animationDelay: animated ? `${index * 0.05}s` : '0s' }}
    >
      {/* Question Row */}
      <div className="ccl-question-row">
        <div className="ccl-question-content">
          <p className="ccl-question-text">{question.questionText}</p>
          {question.helpText && (
            <button
              className="ccl-help-btn"
              onClick={onToggleHelp}
              aria-expanded={isHelpShowing}
              title="More info"
            >
              <HelpCircle size={14} />
            </button>
          )}
        </div>

        {/* Yes/No Toggle */}
        <div className="ccl-answer-toggle">
          <button
            className={`ccl-toggle-btn ccl-toggle-btn--yes ${isYes ? 'ccl-toggle-btn--active' : ''}`}
            onClick={() => onAnswer(question.questionId, true)}
            aria-pressed={isYes}
          >
            <Check size={16} />
            <span>Yes</span>
          </button>
          <button
            className={`ccl-toggle-btn ccl-toggle-btn--no ${isNo ? 'ccl-toggle-btn--active' : ''}`}
            onClick={() => onAnswer(question.questionId, false)}
            aria-pressed={isNo}
          >
            <X size={16} />
            <span>No</span>
          </button>
        </div>
      </div>

      {/* Help Text (expandable) */}
      {question.helpText && (
        <div className={`ccl-help-panel ${isHelpShowing ? 'ccl-help-panel--open' : ''}`}>
          <div className="ccl-help-content">
            <Lightbulb size={14} />
            <span>{question.helpText}</span>
          </div>
        </div>
      )}

      {/* Evidence Section (for Yes answers) */}
      {showEvidenceFields && isYes && (
        <div className="ccl-evidence-section">
          <button
            className="ccl-evidence-toggle"
            onClick={onToggleEvidence}
            aria-expanded={isEvidenceExpanded}
          >
            <FileText size={14} />
            <span>Add evidence notes</span>
            {isEvidenceExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          <div className={`ccl-evidence-panel ${isEvidenceExpanded ? 'ccl-evidence-panel--open' : ''}`}>
            {question.evidenceHint && (
              <p className="ccl-evidence-hint">
                <strong>Suggested evidence:</strong> {question.evidenceHint}
              </p>
            )}
            <textarea
              className="ccl-evidence-input"
              placeholder="Document your evidence or notes here..."
              value={evidenceNotes || ''}
              onChange={(e) => onEvidenceChange(question.questionId, e.target.value)}
              rows={3}
            />
          </div>
        </div>
      )}

      {/* Level indicator dot */}
      <div className="ccl-level-indicator">
        <MaturityLevelDot level={question.impliesLevel} size="sm" />
      </div>
    </div>
  );
}

/**
 * CapabilityProgress - Summary of capability completion by level
 */
export function CapabilityProgress({ questions, answers, animated = true }) {
  const progressByLevel = useMemo(() => {
    return MATURITY_LEVELS.map(level => {
      const levelQuestions = questions.filter(q => q.impliesLevel === level.key);
      const answeredYes = levelQuestions.filter(q => answers[q.questionId]?.answer === true).length;
      return {
        ...level,
        total: levelQuestions.length,
        achieved: answeredYes,
        percentage: levelQuestions.length > 0 ? (answeredYes / levelQuestions.length) * 100 : 0
      };
    });
  }, [questions, answers]);

  return (
    <div className={`ccl-progress ${animated ? 'ccl-progress--animated' : ''}`}>
      {progressByLevel.map((level, idx) => (
        <div
          key={level.key}
          className="ccl-progress-item"
          style={{ animationDelay: animated ? `${idx * 0.1}s` : '0s' }}
        >
          <div className="ccl-progress-label">
            <MaturityLevelDot level={level.key} size="sm" />
            <span>{level.label}</span>
            <span className="ccl-progress-count">{level.achieved}/{level.total}</span>
          </div>
          <div className="ccl-progress-bar">
            <div
              className="ccl-progress-bar-fill"
              style={{
                width: `${level.percentage}%`,
                backgroundColor: level.color
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * DerivedLevelCard - Shows the derived maturity level with explanation
 */
export function DerivedLevelCard({
  derivedLevel,
  hasOverride = false,
  overrideReason = '',
  onOverride,
  animated = true
}) {
  const levelData = MATURITY_LEVELS.find(m => m.key === derivedLevel);

  return (
    <div
      className={`ccl-derived-card ${animated ? 'ccl-derived-card--animated' : ''}`}
      style={{ '--level-color': levelData?.color }}
    >
      <div className="ccl-derived-card-header">
        <span className="ccl-derived-card-label">Derived Maturity Level</span>
        <MaturityLevelBadge level={derivedLevel} size="lg" animated />
      </div>

      <p className="ccl-derived-card-desc">
        {levelData?.description}
      </p>

      {hasOverride && (
        <div className="ccl-override-badge">
          <span>Manual override applied</span>
          {overrideReason && <span className="ccl-override-reason">: {overrideReason}</span>}
        </div>
      )}

      {onOverride && (
        <button className="ccl-override-btn" onClick={onOverride}>
          {hasOverride ? 'Change Override' : 'Override Level'}
        </button>
      )}
    </div>
  );
}
